import { Checkbox, RadioGroup, Switch } from 'radix-ui';
import { Check, Minus } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/* Radix gives these controls the keyboard behaviour, ARIA state and label
   association that the hand-rolled `<button>` toggles they replace never had. */

export const Toggle = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Switch.Root> & { size?: 'sm' | 'md' }
>(function Toggle({ className, size = 'md', ...props }, ref) {
  const track = size === 'sm' ? 'h-4 w-7' : 'h-5 w-9';
  const thumb = size === 'sm' ? 'size-3 data-[state=checked]:translate-x-3.5' : 'size-4 data-[state=checked]:translate-x-4';
  return (
    <Switch.Root
      ref={ref}
      className={cn(
        'peer inline-flex shrink-0 items-center rounded-full border border-transparent p-0.5 transition-colors',
        'bg-border-strong data-[state=checked]:bg-accent',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        'disabled:cursor-not-allowed disabled:opacity-50',
        track,
        className,
      )}
      {...props}
    >
      <Switch.Thumb
        className={cn('block rounded-full bg-white shadow-xs transition-transform duration-150', thumb)}
      />
    </Switch.Root>
  );
});

export const CheckboxControl = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Checkbox.Root>
>(function CheckboxControl({ className, ...props }, ref) {
  return (
    <Checkbox.Root
      ref={ref}
      className={cn(
        'peer inline-flex size-4 shrink-0 items-center justify-center rounded-xs border border-border-strong bg-surface transition-colors',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-accent-fg',
        'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent data-[state=indeterminate]:text-accent-fg',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <Checkbox.Indicator className="flex items-center justify-center">
        {props.checked === 'indeterminate' ? <Minus className="size-3" /> : <Check className="size-3" strokeWidth={3} />}
      </Checkbox.Indicator>
    </Checkbox.Root>
  );
});

export interface ToggleRowProps {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

/** A switch with its label and explanation — one clickable row. */
export function ToggleRow({ label, hint, checked, onCheckedChange, disabled, className }: ToggleRowProps) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-border bg-surface p-3 transition-colors',
        'hover:border-border-strong has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60',
        className,
      )}
    >
      <span className="min-w-0">
        <span className="block text-[0.8125rem] font-medium text-fg">{label}</span>
        {hint && <span className="mt-0.5 block text-xs leading-snug text-fg-muted">{hint}</span>}
      </span>
      <Toggle checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} aria-label={label} />
    </label>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Accessible name when `label` is an icon. */
  title?: string;
}

export interface SegmentedProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  size?: 'sm' | 'md';
  className?: string;
  'aria-label': string;
}

/**
 * A segmented control — a radio group that looks like a switchboard. Used where
 * two-to-four mutually exclusive modes must all stay visible (grid/list, chart/
 * dashboard target, light/dark/system).
 */
export function Segmented<T extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  className,
  ...props
}: SegmentedProps<T>) {
  return (
    <RadioGroup.Root
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      orientation="horizontal"
      className={cn('inline-flex items-center gap-0.5 rounded-md border border-border bg-surface-subtle p-0.5', className)}
      {...props}
    >
      {options.map((o) => (
        <RadioGroup.Item
          key={o.value}
          value={o.value}
          title={o.title}
          aria-label={o.title}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 rounded-sm font-medium transition-colors',
            'text-fg-muted hover:text-fg',
            'data-[state=checked]:bg-surface data-[state=checked]:text-fg data-[state=checked]:shadow-xs',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
            size === 'sm' ? 'h-6 px-2 text-xs [&_svg]:size-3.5' : 'h-7 px-2.5 text-[0.8125rem] [&_svg]:size-4',
          )}
        >
          {o.label}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
}
